import { DOCUMENT } from '@angular/common';
import { inject, Injectable, NgZone } from '@angular/core';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { IModalService } from 'shared-components';

import { TranslationService } from 'app/core/internationalization/translation.service';
import { Logger } from 'app/core/logger/services/logger.service';
import { ModalModel } from 'app/core/modals/services/modal.model';
import { A11yFocusService } from 'app/shared/accessibility/services/a11y-focus.service';
import { WatchVideoComponent } from 'app/shared/modals/watch-video/watch-video.component';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { IConfirmDialogData } from '../confirm-dialog/confirm-dialog-data';
import { InputDialogComponent } from '../input-dialog/input-dialog.component';
import { IInputDialogData } from '../input-dialog/input-dialog-data';

@Injectable({
  providedIn: 'root'
})
export class ModalService implements IModalService {
  private static readonly BUTTON_KEYS = {
    YES: 'MODAL.BUTTONS_YES',
    NO: 'MODAL.BUTTONS_NO',
    LEAVE: 'MODAL.BUTTONS_LEAVE',
    CANCEL: 'MODAL.BUTTONS_CANCEL',
    SUBMIT: 'MODAL.BUTTONS_SUBMIT'
  } as const;

  private readonly modalService = inject(BsModalService);
  private readonly logger = inject(Logger);
  private readonly translationService = inject(TranslationService);
  private readonly document = inject(DOCUMENT);
  private readonly ngZone = inject(NgZone);
  private readonly focusService = inject(A11yFocusService);
  private readonly subscriptions = new Subscription();

  public constructor() {
    this.initializeModalLifecycleHooks();
  }

  private initializeModalLifecycleHooks(): void {
    this.subscriptions.add(this.modalService.onShow.subscribe(() => {
      this.focusService.capturePreviousFocus();
    }));

    this.subscriptions.add(this.modalService.onShown.subscribe(() => {
      this.ngZone.runOutsideAngular(() => setTimeout(() => {
        const modalContainer = this.getLatestModalContainer();

        if (modalContainer) {
          this.enhanceModalAccessibility(modalContainer);
        }
      }));
    }));

    this.subscriptions.add(this.modalService.onHidden.subscribe(() => {
      this.focusService.cleanup();
      this.focusService.restoreFocus();

      if (this.modalService.getModalsCount() <= 0) {
        this.normalizeBodyModalState();
      }
    }));
  }

  private getLatestModalContainer(): HTMLElement | null {
    const containers = Array.from(this.document.querySelectorAll<HTMLElement>('modal-container'));

    return containers.length ? containers[containers.length - 1] : null;
  }

  private enhanceModalAccessibility(dialogElement: HTMLElement): void {
    this.focusService.trapFocus(dialogElement);
    this.focusService.focusInitialElement(dialogElement);
  }

  private normalizeBodyModalState(): void {
    this.document.body.classList.remove('modal-open');
    this.document.body.style.removeProperty('padding-right');
    this.document.body.style.removeProperty('overflow');
    this.document.body.style.setProperty('overflow-y', '');
  }

  public displayModal<TData>(
    component: any,
    data: Partial<TData> = {},
    size = 'md',
    allowEscape: boolean | 'static' = 'static',
    backdrop: boolean | 'static' = false,
    centered = false
  ): BsModalRef<TData> {
    const centerClass = centered ? ' modal-dialog-centered' : '';

    return this.modalService.show(component, {
      backdrop: allowEscape || backdrop,
      class: `modal-${size} ${centerClass}`,
      ignoreBackdropClick: !allowEscape,
      initialState: data,
      keyboard: allowEscape === 'static' ? false : allowEscape
    });
  }

  public displayCustomModal<TData>(component: any, options?: ModalModel<TData>): BsModalRef<TData> {
    const size = options?.size ? `modal-${options.size}` : 'modal-md';

    return this.modalService.show(component, {
      backdrop: options?.backdrop ?? true,
      class: `${size} ${options?.className ?? ''}`,
      ignoreBackdropClick: options?.ignoreBackdropClick ?? false,
      initialState: options?.data,
      keyboard: options?.keyboard ?? true,
      animated: options?.animated ?? true
    });
  }

  public displayTwoButtonConfirm(
    title: string,
    message: string,
    firstButtonText: string,
    secondButtonText: string,
    firstButtonHandler: () => void
  ): void {
    this.displayModal<IConfirmDialogData>(ConfirmDialogComponent, {
      body: this.translationService.instant(message),
      firstButtonHandler,
      firstButtonText,
      secondButtonText,
      title
    }, 'sm', true);
  }

  public displayYesNoConfirm(title: string, message: string, yesCallback: () => void): void {
    this.displayTwoButtonConfirm(
      title,
      message,
      ModalService.BUTTON_KEYS.YES,
      ModalService.BUTTON_KEYS.NO,
      yesCallback
    );
  }

  public displayLeaveCancel(
    title: string,
    message: string,
    leaveCallback: () => void,
    cancelCallback: () => void
  ): void {
    this.displayModal<IConfirmDialogData>(ConfirmDialogComponent, {
      body: this.translationService.instant(message),
      firstButtonHandler: leaveCallback,
      firstButtonText: ModalService.BUTTON_KEYS.LEAVE,
      secondButtonHandler: cancelCallback,
      secondButtonText: ModalService.BUTTON_KEYS.CANCEL,
      title
    }, 'sm', true);
  }

  public displayInputModal(title: string, message: string, yesCallback: (value: string) => void): void {
    this.displayModal<IInputDialogData>(InputDialogComponent, {
      body: this.translationService.instant(message),
      firstButtonHandler: yesCallback,
      firstButtonText: ModalService.BUTTON_KEYS.SUBMIT,
      secondButtonText: ModalService.BUTTON_KEYS.CANCEL,
      title
    }, 'sm', true);
  }

  public showVideo(videoId: string, titile = '', description = ''): void {
    this.displayModal<WatchVideoComponent>(WatchVideoComponent, {
      description,
      title: titile,
      videoId
    }, 'lg', true);
  }

  public showInputBlocker(durationInMS?: number, context?: string): () => void {
    const modalRef = this.modalService.show('', {
      backdrop: true,
      ignoreBackdropClick: true,
      keyboard: false
    });

    if (durationInMS && durationInMS > 0) {
      const timeoutId = setTimeout(() => {
        modalRef.hide();

        this.logger.error(
          `[InputBlocker] Timed out after ${durationInMS}ms while waiting for: ${context || 'unknown context'}.`
        );
      }, durationInMS);

      return () => {
        modalRef.hide();
        clearTimeout(timeoutId);
      };
    }

    return () => modalRef.hide();
  }

  public closeAllModals(): void {
    const modalBackdrops = this.document.querySelectorAll('bs-modal-backdrop');
    const modals = this.document.querySelectorAll('modal-container');

    modalBackdrops.forEach((backdrop) => {
      backdrop.remove();
    });
    modals.forEach((modal) => {
      modal.remove();
    });

    this.normalizeBodyModalState();
  }
}
